import React, { useEffect, useState } from "react";
import { Redirect } from "react-router-dom";

import {
  Button,
  Card,
  CardStackSection,
  Heading,
  MarkdownLite,
  Stack,
  Text,
} from "@origin-digital/ods-core";
import { required } from "@origin-digital/validation";
import { reportException } from "@origin-digital/reporting-client";

import { useTrackingContext } from "src/context/TrackingContext";
import { useTextFieldState } from "src/hooks";
import {
  reduceValidators,
  transformPhone,
  transformPhoneFromInternational,
  transformPhoneToInternational,
} from "src/utils/helpers";
import { errorTracking, formTracking } from "src/utils/tracking";
import {
  hideNotification,
  showErrorNotification,
} from "src/utils/notifications";
import { Form } from "src/components/Form";
import { TextField } from "src/components/TextField";

import { IValidatorFunction } from "@origin-digital/validation/src/types";
import { FieldUpdateHook } from "../../../crm/profileFieldMap";
import { ProfileFieldMapTypes } from "../../../crm/profileFieldMapContextHelpers";

export type UpdatePhoneType = "mobile" | "landline" | "homephone";

export interface IProps {
  value: string | null;
  content: {
    heading: string;
    message: string;
    phone: {
      label: string;
      helperText?: string;
      errors: {
        required: string;
      };
    };
    submit: string;
    remove?: string;
    submitError: string;
  };
  useUpdatePhone: FieldUpdateHook<string>;
  updateSuccessRoute: string;
  removeSuccessRoute?: string;
  type: UpdatePhoneType;
  validator: IValidatorFunction;
  fieldMapType: ProfileFieldMapTypes;
}

export const UpdatePhone: React.FC<IProps> = ({
  content,
  updateSuccessRoute,
  removeSuccessRoute,
  useUpdatePhone,
  type,
  validator,
  value,
  fieldMapType,
}) => {
  const [updatePhone, { success, loading, error }] = useUpdatePhone();
  const [successRoute, setSuccessRoute] = useState(updateSuccessRoute);
  const trackingContext = useTrackingContext();
  const phoneState = useTextFieldState(
    transformPhoneFromInternational(value ?? ""),
    content.phone.helperText
  );
  const [redirecting, setRedirecting] = useState<boolean>(false);

  useEffect(() => {
    if (error) {
      showErrorNotification(content.submitError);
      errorTracking(error, fieldMapType);
      reportException(`Update${type}Screen.updatePhone()`, error);
    }
  }, [error, content.submitError, type]);

  useEffect(() => {
    if (success && !error) {
      formTracking("complete", trackingContext, fieldMapType);
      setRedirecting(true);
    }
  }, [success, error]);

  const handleValidate = (): string => {
    const { errors } = content.phone;

    const validators = [required({ error: errors.required }), validator];

    const errorMessage = reduceValidators(phoneState.value, validators);

    phoneState.setError(errorMessage);

    return errorMessage;
  };

  const handleUpdate = (phone: string, validate: boolean) => {
    formTracking("start", trackingContext, fieldMapType);

    if (validate) {
      if (handleValidate()) {
        return;
      }
    }

    phoneState.setFocus(false);
    hideNotification();

    updatePhone(phone);
  };

  const handleSubmit = () => {
    setSuccessRoute(updateSuccessRoute);
    handleUpdate(transformPhoneToInternational(phoneState.value), true);
  };

  const handleRemove = () => {
    setSuccessRoute(removeSuccessRoute!);
    handleUpdate("", false);
  };

  const shouldShowRemove = () => {
    return type === "landline" && !!value;
  };

  const shouldShowSpinner = (completeRoute?: string) => {
    return loading && successRoute === completeRoute;
  };

  const shouldDisable = (completeRoute?: string) => {
    return loading && successRoute !== completeRoute;
  };

  if (redirecting) {
    return <Redirect push to={successRoute} />;
  }

  return (
    <CardStackSection data-id={`update-${type}`}>
      <Card>
        <Form onSubmit={handleSubmit}>
          <Stack space={["large", "xlarge"]}>
            <Heading data-id="heading" variant="h3">
              {content.heading}
            </Heading>
            <Stack space={["medium", "large"]}>
              <Text data-id="phone-message">
                <MarkdownLite>{content.message}</MarkdownLite>
              </Text>
              <TextField
                characterCount
                disabled={loading}
                handleValidate={handleValidate}
                id="phone"
                data-id="phone"
                label={content.phone.label}
                maxLength={10}
                textFieldState={phoneState}
                transform={transformPhone}
                type="tel"
                onFocus={() =>
                  formTracking("start", trackingContext, fieldMapType)
                }
              />
            </Stack>
            <Button
              data-id="submit"
              type="submit"
              spinner={shouldShowSpinner(updateSuccessRoute)}
              disabled={shouldDisable(updateSuccessRoute)}
              fullWidth
            >
              {content.submit}
            </Button>

            {shouldShowRemove() && (
              <Stack alignX="center">
                <Button
                  noTextPadding
                  data-id="delete"
                  variant="text"
                  fullWidth
                  type="button"
                  spinner={shouldShowSpinner(removeSuccessRoute)}
                  disabled={shouldDisable(removeSuccessRoute)}
                  onClick={handleRemove}
                >
                  {content.remove}
                </Button>
              </Stack>
            )}
          </Stack>
        </Form>
      </Card>
    </CardStackSection>
  );
};
